/**
@format
@flow
*/
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import Colors from '@ziynx/theme/colors';
import Icon from '@ziynx/components/icons';
import Image, { ImageAvatar } from '@ziynx/components/images';
import Link from '@ziynx/components/links';
import { withAppManager } from '@ziynx/components/utilities';
import { Body, SubHeading } from '@ziynx/components/typography';
import { Button } from '@ziynx/components/buttons';
import { Popover } from '@krispy/popovers/web';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { ViewportType } from '@ziynx/flow-types/viewports';
import SideNavList from './SideNavList';
import MenuButton from './MenuButton';
import ziynxLogo from '../../assets/ZIYNX_LOGO_2.png';
import ziynxSymbol from '../../assets/ZIYNX_SYMBOL_2 2x.png';

type Props = $ReadOnly<{|

  /**
   * Content passed in wrapper
   */
  content: Array<Object>,

  /**
   * Top portion of side nav
   * ProfileImage, name, and company name
   */
  topContent?: {
    image?: {
      uri: string,
      placeholderText?: string,
    },
    heading?: {
      children: string,
    },
    subHeading?: {
      children: string,
    },
  },

  /**
   * Pass in navigation for logout button here
   */
  logoutOnClick?: (event: SyntheticEvent<HTMLAnchorElement | HTMLButtonElement>) =>
    any | Promise<any>,

  expandedBottomAction?: {
    onClick?: (evt: SyntheticEvent<HTMLButtonElement>) => void,
    popover: {
      trigger: React$Element<'button'>,
      children: any,
      position?: 'topRight' | 'bottomRight' | 'topLeft' | 'bottomLeft',
    },
  },
|}>;

function getWidthExpanded(viewport: ViewportType) {
  let width;
  switch (viewport) {
  case 'desktop':
    width = '296px';
    break;
  case 'desktopSmall':
  case 'tablet':
    width = '414px';
    break;
  case 'mobile':
  case 'mobileSmall':
  default:
    // In case calc is not supported
    return `
      width: 96%;
      width:calc(100% - 24px);`;
  }
  return `width: ${width}`;
}

const StyledMenuButton = styled(MenuButton)`
  transform: translateX(0);
  transition: 400ms;
  position: absolute;
  top: 20px;
  left: 21px;
  padding-top: 20px;
  z-index: 987;
 ${({ sideNavExpanded }) => sideNavExpanded
  && `
    transform: translateX(240px);
    transition: transform 400ms;
    transition-delay: 100ms;
  `}
`;

const SearchbarPlaceholder = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 16px;
  margin: 0px 4px 16px;
  padding-left: 16px;
  height: 46px;
  box-shadow: 0 4px 14px rgba(166,179,194,0.3);
`;

const SearchInputWrapper = styled.div`
  margin-left: 12px;
`;

function calculateOffsetTop(viewport) {
  let offsetTop;
  switch (viewport) {
  case 'desktop':
    offsetTop = 0;
    break;
  case 'desktopSmall':
  case 'tablet':
    offsetTop = 96;
    break;
  case 'mobile':
  case 'mobileSmall':
  default:
    offsetTop = 84;
    break;
  }
  return `${offsetTop}px`;
}

const getMargin = (desktop: boolean, logout: boolean): string => {
  if (desktop) {
    return '76px';
  }
  if (logout) {
    return '0px';
  }
  return '32px';
};

const SidebarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${({ sideNavExpanded }) => !sideNavExpanded && 'overflow: hidden;'}
  height: ${({ viewport }) => (viewport === 'desktopSmall' || viewport === 'tablet' ? 'auto' : '100vh')};
  max-height: ${({ viewport }) => (viewport === 'desktopSmall' || viewport === 'tablet' ? '672px' : undefined)};
  ${({ isDesktop }) => (isDesktop ? 'position: sticky; float: left;' : 'position: fixed; left: 0;')};
  width: ${({ isDesktop }) => (!isDesktop ? 0 : '68px')};
  transition: width 400ms, padding 400ms linear;
  padding-left: ${({ isDesktop, sideNavExpanded }) => (!isDesktop && !sideNavExpanded ? 0 : '12px')};
  padding-right: ${({ isDesktop, sideNavExpanded }) => (!isDesktop && !sideNavExpanded ? 0 : '12px')};
  justify-content:  ${({ isDesktop }) => (isDesktop ? 'space-between' : undefined)};
  background-color: ${Colors.White};
  top: 0;
  z-index: ${({ isDesktop }) => (isDesktop ? 986 : 982)};
  box-shadow: 0 4px 14px 0 rgba(166,179,194,0.3);
  ${({ sideNavExpanded, viewport }) => sideNavExpanded
  && `
    ${getWidthExpanded(viewport)};
    transition: width 400ms;
  `}
`;

const NavigationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: ${({ isDesktop, viewport }) => (isDesktop ? 0 : `calc(${calculateOffsetTop(viewport)} + 32px)`)};
  width: 100%;
`;

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  box-shadow: ${({ showLogout }) => (showLogout ? '0 4px 14px 0 rgba(166,179,194,0.3)' : undefined)};
  margin-bottom: ${({ showLogout }) => (showLogout && '32px')};
  padding: ${({ showLogout }) => (showLogout ? '0px 16px' : undefined)};
  padding-bottom: ${({ showLogout }) => (showLogout && '16px')};
`;

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: ${({ isDesktop }) => (isDesktop ? 'auto' : '76px')};
  justify-content: space-between;
  align-items: center;
  border-radius: 16px;
  box-shadow: ${({ isDesktop, showLogout }) => (isDesktop || showLogout ? undefined : '0 4px 14px 0 rgba(166,179,194,0.3)')};
  padding: ${({ showLogout }) => (showLogout ? undefined : '0px 16px')};
  margin-top: ${({ isDesktop }) => (isDesktop && '24px')};
  margin-bottom: ${({ isDesktop, showLogout }) => getMargin(isDesktop, showLogout)};
  img {
    border-radius: 16px;
  };
`;

const ProfileHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  opacity: 0;
  transition: opacity 200ms;
  min-width: 0;
  ${({ sideNavExpanded }) => sideNavExpanded
&& `
  opacity: 1;
  transition: opacity 200ms;
  transition-delay: 200ms;
`
}
`;

const NameAndCompanyWrapper = styled.div`
  min-width: 0;
  padding: 0 12px;
  white-space: nowrap;
  ${({ isDesktop }) => isDesktop && 'padding-right: 32px;'}
`;

const StyledBody = styled(Body)`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const BottomContentWrapper = styled.div``;

const LogoWrapper = styled.div`
  padding-bottom: 25px;
  padding-left: 24px;
  padding-right: 24px;
  transition: padding-left 400ms ease-in;
  ${({ sideNavExpanded }) => sideNavExpanded
  && `
    padding-left: 16px;
    transition: padding-left 400ms ease-in;
  `
}
`;

const ZiynxLogoX = styled(Image)`
  opacity: 1;
  position: relative;
  transition: opacity 400ms;
  transition-delay: 300ms;
  ${({ sideNavExpanded }) => sideNavExpanded
&& `
  opacity: 0;
  transition: opacity 400ms;
`}
`;

const ZiynxLogoWhole = styled(Image)`
  position: absolute;
  bottom: 25px;
  opacity: 0;
  transition: opacity 400ms;
  ${({ sideNavExpanded }) => sideNavExpanded
&& `
  opacity: 1;
  transition: opacity 400ms;
  transition-delay: 200ms;
`
}
`;

const AlertsIconWrapper = styled.div`
  position: absolute;
  right: 25px;
  bottom: 23px;
  opacity: 0;
  transition: opacity 400ms;
  cursor: auto;
  ${({ sideNavExpanded }) => sideNavExpanded
&& `
  opacity: 1;
  transition: opacity 400ms;
  cursor: pointer;
`
}
`;

const MobileIconWrapper = styled.div`
  cursor: pointer;
`;

const SupportWrapper = styled.div`
  display: flex;
  opacity: 0;
  white-space: nowrap;
  margin-bottom: ${({ isDesktop }) => (isDesktop ? '60px' : '32px')};
  padding-left: 16px;
  ${({ sideNavExpanded }) => sideNavExpanded
 && `
    opacity: 1;
    transition: opacity 450ms ease;
  `
}
`;

const LinkWrapper = styled.div``;

function renderTop(isDesktop: boolean, sideNavExpanded: boolean, topContent: Object) {
  const { image, heading, subHeading } = topContent || {};
  // temp until we need it
  const shouldRender = false;
  if (isDesktop) {
    return (
      <ProfileWrapper sideNavExpanded={sideNavExpanded} isDesktop={isDesktop} showLogout={false}>
        <ProfileHeadingWrapper sideNavExpanded={sideNavExpanded}>
          <ImageAvatar size="medium" imageSource={image?.uri} placeholderText={image?.placeholderText} />
          <NameAndCompanyWrapper isDesktop={isDesktop}>
            <StyledBody medium>{heading?.children}</StyledBody>
            <StyledBody small color={Colors.Gray}>{subHeading?.children}</StyledBody>
          </NameAndCompanyWrapper>
        </ProfileHeadingWrapper>
      </ProfileWrapper>
    );
  }

  if (!isDesktop && shouldRender) {
    return (
      <SearchbarPlaceholder>
        <Icon veryTiny name="search" />
        <SearchInputWrapper>
          <Body medium color={Colors.Gray}>Search</Body>
        </SearchInputWrapper>
      </SearchbarPlaceholder>
    );
  }

  return null;
}

function renderBottom(
  isDesktop: boolean,
  sideNavExpanded: boolean,
  sideNavAppHandler: Function,
  expandedBottomAction: $PropertyType<Props,
  "expandedBottomAction">,
  topContent,
  showLogout: boolean,
  setShowLogout,
  logoutOnClick,
) {
  const { image, heading, subHeading } = topContent || {};

  // Cannot directly setShowLogout or it breaks
  const toggleShowLogout = (): void => {
    setShowLogout(!showLogout);
  };

  const icon = showLogout ? 'chevron-up' : 'chevron-down';

  if (!isDesktop) {
    return (
      <BottomContentWrapper>
        <ComponentWrapper showLogout={showLogout}>
          <ProfileWrapper
            sideNavExpanded={sideNavExpanded}
            isDesktop={isDesktop}
            showLogout={showLogout}
            onClick={toggleShowLogout}
          >
            <ProfileHeadingWrapper sideNavExpanded={sideNavExpanded}>
              <ImageAvatar size="medium" imageSource={image?.uri} placeholderText={image?.placeholderText} />
              <NameAndCompanyWrapper isDesktop={isDesktop}>
                <StyledBody medium>{heading?.children}</StyledBody>
                <StyledBody small color={Colors.Gray}>{subHeading?.children}</StyledBody>
              </NameAndCompanyWrapper>
            </ProfileHeadingWrapper>
            <MobileIconWrapper>
              <Icon mini color={Colors.Gray} name={icon} />
            </MobileIconWrapper>
          </ProfileWrapper>
          {showLogout && <Button variant="primary" onClick={logoutOnClick} width="100% - 32px">Log Out</Button>}
        </ComponentWrapper>
        <SupportWrapper sideNavExpanded={sideNavExpanded} isDesktop={isDesktop}>
          <SubHeading color={Colors.Gray} large>
            Need support?&nbsp;
          </SubHeading>
          <LinkWrapper onClick={() => sideNavAppHandler(false)}>
            <Link
              variant="navigation"
              to="/support"
            >
              Contact us
            </Link>
          </LinkWrapper>
        </SupportWrapper>
      </BottomContentWrapper>
    );
  }
  return (
    <BottomContentWrapper>
      <SupportWrapper sideNavExpanded={sideNavExpanded} isDesktop={isDesktop}>
        <SubHeading color={Colors.Gray} large>
          Need support?&nbsp;
        </SubHeading>
        <LinkWrapper>
          <Link
            variant="navigation"
            to="/support"
          >
            Contact us
          </Link>
        </LinkWrapper>
      </SupportWrapper>
      <LogoWrapper sideNavExpanded={sideNavExpanded}>
        <AlertsIconWrapper
          sideNavExpanded={sideNavExpanded}
          onClick={expandedBottomAction?.onClick}
        >
          { sideNavExpanded && expandedBottomAction?.popover
        && expandedBottomAction?.popover?.trigger
        && <Popover {...expandedBottomAction?.popover} /> }
        </AlertsIconWrapper>
        <ZiynxLogoX sideNavExpanded={sideNavExpanded} width="20px" height="20px" src={ziynxSymbol} />
        <ZiynxLogoWhole sideNavExpanded={sideNavExpanded} width="100px" height="20px" src={ziynxLogo} />
      </LogoWrapper>
    </BottomContentWrapper>
  );
}

const SideNavBar = (props: ManagedProps<Props>): React$Node => {
  const {
    content,
    viewport,
    sideNavExpanded,
    expandedBottomAction,
    topContent,
    sideNavAppHandler,
    logoutOnClick,
  } = props;

  const location = useLocation();
  const showNav = location?.pathname !== '/profile-setup';
  const isDesktop = viewport === 'desktop';
  const [showLogout, setShowLogout] = useState(false);

  useEffect(() => () => {
    if (sideNavAppHandler) {
      // If the side nav is going to unmount make sure we close it.
      sideNavAppHandler(false);
    }
  }, []);
  return (
    showNav ? (
      <>
        <SidebarWrapper
          sideNavExpanded={sideNavExpanded}
          viewport={viewport}
          isDesktop={isDesktop}
        >
          <NavigationWrapper isDesktop={isDesktop} viewport={viewport}>
            {isDesktop && (
              <StyledMenuButton
                sideNavExpanded={sideNavExpanded}
                isDesktop={isDesktop}
              />
            )}
            {renderTop(isDesktop, !!sideNavExpanded, topContent)}
            <SideNavList
              content={content}
              viewport={viewport}
              sideNavAppHandler={sideNavAppHandler}
              isCollapsed={!sideNavExpanded}
            />
          </NavigationWrapper>
          {renderBottom(
            isDesktop,
            !!sideNavExpanded,
            sideNavAppHandler,
            expandedBottomAction,
            topContent,
            showLogout,
            setShowLogout,
            logoutOnClick,
          )}
        </SidebarWrapper>
      </>
    ) : null
  );
};

SideNavBar.defaultProps = {
  sideNavExpanded: false,
};

export default (withAppManager(SideNavBar): React$ComponentType<React$Config<
  ManagedProps<Props>, typeof SideNavBar.defaultProps
>>);
